import { SocketTopics } from "@shared/types";
import { combineReducers, createSelector, createSlice } from "@reduxjs/toolkit";
import {
  SocketArgs,
  SocketDataState,
  TopicReducerHandler,
} from "@shared/services/socket/types";
import { topicReducer } from "@shared/services/socket/constants";
import { skipToken } from "@reduxjs/toolkit/query";
import { get, mapValues } from "lodash";

const createSocketSlice = <Topic extends SocketTopics>(topic: Topic) => {
  const sliceName = "sockets/" + topic;
  const handler = topicReducer[topic] as unknown as TopicReducerHandler<Topic>;

  const initialState: SocketDataState<Topic> = {
    rawSocketData: undefined,
    prevRawSocketData: undefined,
  };

  const socketsSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
      updateSocketData(state, action) {
        return {
          prevRawSocketData: state.rawSocketData,
          rawSocketData: action.payload,
          timestamp: new Date().getTime(),
        };
      },
    },
  });

  const selectData = (state: any): SocketDataState<Topic> => {
    return get(state, sliceName.split("/"));
  };

  const getArgsDataSelector = (args: SocketArgs<Topic> | typeof skipToken) =>
    createSelector(
      selectData,
      (state) => {
        return {
          value: handler.getData(args, state?.rawSocketData),
          previous: handler.getData(args, state?.prevRawSocketData),
          timestamp: state?.timestamp,
        };
      },
      {
        memoizeOptions: {
          resultEqualityCheck: (a, b) => {
            if (typeof a.value === "number") {
              return a.value === b.value && a.previous === b.previous;
            }
            return a === b;
          },
        },
      }
    );

  return {
    reducer: socketsSlice.reducer,
    actions: socketsSlice.actions,
    topic,
    selectors: {
      selectData,
      getArgsDataSelector,
    },
  };
};

export const socketsSlices: {
  [key in SocketTopics]: ReturnType<typeof createSocketSlice>;
} = Object.values(SocketTopics).reduce((reducersMapping, topic) => {
  reducersMapping[topic] = createSocketSlice(topic);
  return reducersMapping;
}, {} as any);

export const socketsReducer = combineReducers(
  mapValues(socketsSlices, (slice) => slice.reducer)
);
